// screens/business/MultiUserBusinessSignup.js
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

import { useBusiness } from '../../../context/BusinessContext';
import { storage } from '../../../utils/storage';

const API_BASE_URL = 'https://api.jobeng.ai/api';
const DATA_API_URL = `${API_BASE_URL}/data`; // Dynamic data endpoint
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

export default function MultiUserBusinessSignup({ navigation }) {
  const {
    saveUser,
    saveCompanyProfile,
    saveSubscription,
    addTeamMember: contextAddTeamMember,
    addNotification,
    isOnline,
    apiAvailable,
    syncAllData
  } = useBusiness();

  const [step, setStep] = useState(1);
  const [signupType, setSignupType] = useState('new_company');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [serverStatus, setServerStatus] = useState('unknown');

  // Company Information (Step 1)
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [companyCode, setCompanyCode] = useState('');

  // User Information (Step 2)
  const [fullName, setFullName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [userRole, setUserRole] = useState('employee');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Team Setup (Step 3)
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('employee');
  const [newMemberDepartment, setNewMemberDepartment] = useState('');

  // Subscription Plan (Step 4 - only for new_company)
  const [selectedPlan, setSelectedPlan] = useState(null);

  // UI state
  const [isFocused, setIsFocused] = useState({
    companyName: false,
    companyEmail: false,
    industry: false,
    companySize: false,
    fullName: false,
    userEmail: false,
    jobTitle: false,
    department: false,
    password: false,
    confirmPassword: false,
    newMemberEmail: false,
    newMemberDepartment: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Dynamic total steps based on signup type
  const totalSteps = signupType === 'new_company' ? 4 : 3;

  // Industry options
  const industries = [
    'Construction & Engineering',
    'Civil Engineering',
    'Mechanical Engineering',
    'Electrical Engineering',
    'Software Development',
    'Technology',
    'Architecture',
    'Manufacturing',
    'Healthcare',
    'Finance',
    'Retail',
    'Education',
    'Real Estate',
    'Transportation',
    'Consulting',
    'Other'
  ];

  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '500+ employees'
  ];

  const userRoles = [
    { id: 'admin', title: 'Administrator', description: 'Full access' },
    { id: 'manager', title: 'Manager', description: 'Team management' },
    { id: 'hr_manager', title: 'HR Manager', description: 'HR functions' },
    { id: 'finance_manager', title: 'Finance Manager', description: 'Financial management' },
    { id: 'project_manager', title: 'Project Manager', description: 'Project oversight' },
    { id: 'team_lead', title: 'Team Lead', description: 'Team coordination' },
    { id: 'senior_engineer', title: 'Senior Engineer', description: 'Technical leadership' },
    { id: 'engineer', title: 'Engineer', description: 'Engineering tasks' },
    { id: 'employee', title: 'Employee', description: 'Standard access' }
  ];

  const subscriptionPlans = [
    {
      id: 'multi_team',
      name: 'Team',
      price: 149,
      users: 5,
      features: [
        'Up to 5 users',
        'Basic project management',
        'Standard reporting',
        'Email support',
        '10GB storage'
      ]
    },
    {
      id: 'multi_business',
      name: 'Business',
      price: 299,
      users: 15,
      features: [
        'Up to 15 users',
        'Advanced project management',
        'Custom reporting',
        'Priority support',
        '100GB storage',
        'Team collaboration tools'
      ],
      popular: true
    },
    {
      id: 'multi_enterprise',
      name: 'Enterprise',
      price: 599,
      users: 'Unlimited',
      features: [
        'Unlimited users',
        'Enterprise features',
        'Advanced analytics',
        'Dedicated support',
        'Unlimited storage',
        'Custom integrations',
        'White-label options'
      ]
    }
  ];

  useEffect(() => {
    testServerConnection();
  }, []);

  const testServerConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      // Test the root endpoint instead of /health
      const response = await fetch(`${API_BASE_URL}/`, {
        method: 'GET',
        headers: { 
          'X-API-Key': API_KEY,
          'Accept': 'application/json'
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      setServerStatus(response.ok ? 'connected' : 'error');
    } catch {
      setServerStatus('error');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (field, value) => {
    let error = '';
    switch (field) {
      case 'companyName':
        if (!value || value.trim().length < 2) error = 'Company name must be at least 2 characters';
        break;
      case 'companyEmail':
        if (!value) error = 'Company email is required';
        else if (!validateEmail(value)) error = 'Invalid email format';
        break;
      case 'industry':
        if (!value) error = 'Industry is required';
        break;
      case 'companySize':
        if (!value) error = 'Company size is required';
        break;
      case 'fullName':
        if (!value || value.trim().length < 2) error = 'Full name must be at least 2 characters';
        break;
      case 'userEmail':
        if (!value) error = 'Email is required';
        else if (!validateEmail(value)) error = 'Invalid email format';
        break;
      case 'jobTitle':
        if (!value) error = 'Job title is required';
        break;
      case 'department':
        if (!value) error = 'Department is required';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 6) error = 'Password must be at least 6 characters';
        break;
      case 'confirmPassword':
        if (value !== password) error = 'Passwords do not match';
        break;
      default:
        break;
    }
    setFormErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleInputChange = (field, value) => {
    const setters = {
      companyName: setCompanyName,
      companyEmail: setCompanyEmail,
      industry: setIndustry,
      companySize: setCompanySize,
      fullName: setFullName,
      userEmail: setUserEmail,
      jobTitle: setJobTitle,
      department: setDepartment,
      password: setPassword,
      confirmPassword: setConfirmPassword,
      newMemberEmail: setNewMemberEmail,
      newMemberDepartment: setNewMemberDepartment,
      companyCode: setCompanyCode,
    };
    if (setters[field]) setters[field](value);

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFocus = (field) => {
    setIsFocused(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setIsFocused(prev => ({ ...prev, [field]: false }));
    if (field !== 'newMemberEmail' && field !== 'newMemberDepartment') {
      validateField(field, getFieldValue(field));
    }
  };

  const getFieldValue = (field) => {
    const values = {
      companyName, companyEmail, industry, companySize,
      fullName, userEmail, jobTitle, department,
      password, confirmPassword,
    };
    return values[field] || '';
  };

  const validateStep = () => {
    if (step === 1 && signupType === 'join_company') {
      if (!companyCode) {
        Alert.alert('Error', 'Please enter the company code.');
        return false;
      }
      if (companyCode.length !== 8) {
        Alert.alert('Error', 'Company code must be exactly 8 characters.');
        return false;
      }
      return true;
    }

    const fieldsToValidate = [];
    if (step === 1 && signupType === 'new_company') {
      fieldsToValidate.push('companyName', 'companyEmail', 'industry', 'companySize');
    } else if (step === 2) {
      fieldsToValidate.push('fullName', 'userEmail', 'jobTitle', 'department', 'password', 'confirmPassword');
    } else if (step === 3 && signupType === 'new_company') {
      return true;
    } else if (step === 3 && signupType === 'join_company') {
      return true;
    } else if (step === 4 && signupType === 'new_company') {
      if (!selectedPlan) {
        Alert.alert('Error', 'Please select a subscription plan.');
        return false;
      }
      return true;
    }

    let isValid = true;
    fieldsToValidate.forEach(field => {
      const fieldValid = validateField(field, getFieldValue(field));
      if (!fieldValid) isValid = false;
    });
    return isValid;
  };

  // FIXED: Validate company code by checking company_profiles table
  const validateCompanyCode = async (code) => {
    setIsValidatingCode(true);
    try {
      const response = await fetch(`${DATA_API_URL}/company_profiles?company_code=${code}`, {
        headers: { 
          'X-API-Key': API_KEY,
          'Accept': 'application/json'
        },
      });
      const result = await response.json();
      
      if (result.success && result.data && result.data.length > 0) {
        const company = result.data[0];
        setCompanyName(company.company_name || '');
        setCompanyEmail(company.email || '');
        setIndustry(company.industry || '');
        setCompanySize(company.employee_count ? `${company.employee_count} employees` : '');
        return true;
      } else {
        Alert.alert('Error', 'Company code not found. Please check and try again.');
        return false;
      }
    } catch (error) {
      console.error('Error validating company code:', error);
      Alert.alert('Error', 'Failed to validate company code. Please try again.');
      return false;
    } finally {
      setIsValidatingCode(false);
    }
  };

  // FIXED: Check if email exists in business_users table
  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(
        `${DATA_API_URL}/business_users?email=${encodeURIComponent(email)}`,
        {
          headers: {
            'X-API-Key': API_KEY,
            'Accept': 'application/json'
          }
        }
      );
      const result = await response.json();
      return result.success && result.data && result.data.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (step === 1 && signupType === 'join_company') {
      const isValid = await validateCompanyCode(companyCode);
      if (!isValid) return;
    }

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSignup();
    }
  };

  const handlePrevious = () => {
    if (step > 1) setStep(step - 1);
  };

  const addTeamMemberToState = () => {
    if (!newMemberEmail) {
      Alert.alert('Error', 'Please enter an email address.');
      return;
    }
    if (!validateEmail(newMemberEmail)) {
      Alert.alert('Error', 'Invalid email format.');
      return;
    }
    if (teamMembers.some(m => m.email === newMemberEmail)) {
      Alert.alert('Error', 'This email is already added.');
      return;
    }
    if (newMemberEmail === userEmail) {
      Alert.alert('Error', 'You cannot add yourself as a team member.');
      return;
    }
    const newMember = {
      id: Date.now().toString(),
      email: newMemberEmail,
      name: '',
      role: newMemberRole,
      department: newMemberDepartment || 'General',
      status: 'invited',
      invitedAt: new Date().toISOString()
    };
    setTeamMembers([...teamMembers, newMember]);
    setNewMemberEmail('');
    setNewMemberRole('employee');
    setNewMemberDepartment('');
  };

  const removeTeamMemberFromState = (memberId) => {
    setTeamMembers(teamMembers.filter(m => m.id !== memberId));
  };

  const generateCompanyCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // FIXED: Save user to database using correct table name and without id
  const saveUserToDatabase = async (userData, token) => {
    try {
      // Remove id field to let database auto-generate
      const { id, ...userDataWithoutId } = userData;
      
      const response = await fetch(`${DATA_API_URL}/business_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userDataWithoutId)
      });

      const result = await response.json();
      if (!response.ok) {
        console.warn('Failed to save user to database:', result.error);
        return null;
      }
      return result.data || result;
    } catch (error) {
      console.error('Error saving user to database:', error);
      return null;
    }
  };

  // FIXED: Save company to database using correct table name and without id
  const saveCompanyToDatabase = async (companyData, token) => {
    try {
      // Remove id field to let database auto-generate
      const { id, ...companyDataWithoutId } = companyData;
      
      const response = await fetch(`${DATA_API_URL}/company_profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(companyDataWithoutId)
      });

      const result = await response.json();
      if (!response.ok) {
        console.warn('Failed to save company to database:', result.error);
        return null;
      }
      return result.data || result;
    } catch (error) {
      console.error('Error saving company to database:', error);
      return null;
    }
  };

  // FIXED: Save team members to database using correct table name
  const saveTeamMembersToDatabase = async (members, companyId, token) => {
    try {
      const promises = members.map(member => {
        // Generate a temporary password for invited members
        const tempPassword = Math.random().toString(36).slice(-8);
        
        // First create user account
        return fetch(`${API_BASE_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY
          },
          body: JSON.stringify({
            email: member.email,
            password: tempPassword,
            full_name: member.email.split('@')[0],
            user_type: 'business'
          })
        })
        .then(regResponse => regResponse.json())
        .then(regResult => {
          if (regResult.success && regResult.data?.token) {
            // Then create user profile
            return fetch(`${DATA_API_URL}/business_users`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-API-Key': API_KEY,
                'Authorization': `Bearer ${regResult.data.token}`
              },
              body: JSON.stringify({
                full_name: member.email.split('@')[0],
                email: member.email,
                job_title: member.role,
                department: member.department,
                company_name: companyName,
                company_id: companyId,
                role: member.role,
                access_level: member.role === 'admin' ? 'admin' : 'employee',
                status: 'pending',
                company_id: 1,
                preferences: {
                  notifications: {
                    email: true,
                    push: true,
                    sms: false,
                    marketing: false,
                    security_alerts: true,
                    job_updates: true,
                    team_updates: true
                  },
                  privacy: {
                    profile_visibility: 'team',
                    contact_visibility: 'managers',
                    activity_visibility: 'private',
                    search_visibility: true,
                    data_sharing: false
                  },
                  security: {
                    two_factor_enabled: false,
                    biometric_enabled: false,
                    session_timeout: 30,
                    login_alerts: true,
                    device_tracking: true
                  }
                }
              })
            });
          }
        });
      });
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Error saving team members:', error);
    }
  };

  // FIXED: Save subscription using correct table name
  const saveSubscriptionToDatabase = async (subscriptionData, companyId, token) => {
    try {
      const response = await fetch(`${DATA_API_URL}/company_subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          company_id: companyId,
          plan_id: subscriptionData.plan_id,
          plan_name: subscriptionData.plan_name,
          plan_price: subscriptionData.price,
          billing_cycle: 'monthly',
          status: 'active',
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          features: subscriptionData.features,
          limits: subscriptionData.limits || {},
          created_by: 1
        })
      });

      const result = await response.json();
      if (!response.ok) {
        console.warn('Failed to save subscription:', result.error);
        return null;
      }
      return result.data || result;
    } catch (error) {
      console.error('Error saving subscription:', error);
      return null;
    }
  };

  const handleSignup = async () => {
    try {
      setIsLoading(true);

      // Check if email already exists
      const emailExists = await checkEmailExists(userEmail);
      if (emailExists) {
        Alert.alert(
          'Account Exists',
          'An account with this email already exists. Please sign in instead.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Sign In', onPress: () => navigation.navigate('BusinessLogin') }
          ]
        );
        setIsLoading(false);
        return;
      }

      if (!validateEmail(userEmail)) {
        Alert.alert('Error', 'Invalid user email.');
        return;
      }

      const cleanUserEmail = userEmail.trim().toLowerCase();
      const cleanCompanyEmail = signupType === 'new_company' ? companyEmail.trim().toLowerCase() : companyEmail;

      // Step 1: Register user with auth endpoint
      const registerResponse = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY 
        },
        body: JSON.stringify({
          email: cleanUserEmail,
          password: password,
          full_name: fullName.trim(),
          user_type: 'business',
        }),
      });

      if (!registerResponse.ok) {
        const errorText = await registerResponse.text();
        let errorMessage = `Registration failed: ${registerResponse.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const registerResult = await registerResponse.json();

      if (registerResult.success) {
        const token = registerResult.data?.token;
        const authUserId = registerResult.data?.user?.id;
        const companyId = signupType === 'new_company' ? Date.now().toString() : `company_${Date.now()}`;
        const newCompanyCode = signupType === 'new_company' ? generateCompanyCode() : companyCode;

        // Prepare company data WITHOUT id
        const companyData = {
          company_name: companyName.trim(),
          email: cleanCompanyEmail,
          industry: industry.trim(),
          employee_count: parseInt(companySize?.split('-')[0]) || 1,
          phone: '',
          website: '',
          logo: null,
          address: '',
          description: '',
          vat_number: '',
          registration_number: '',
          legal_name: companyName.trim(),
          company_code: newCompanyCode,
          company_id: 1
        };

        // Prepare user data WITHOUT id
        const userData = {
          full_name: fullName.trim(),
          email: cleanUserEmail,
          phone: '',
          date_of_birth: null,
          address: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          profile_image: null,
          job_title: jobTitle.trim(),
          department: department.trim(),
          employee_id: `EMP${Date.now().toString().slice(-6)}`,
          start_date: new Date().toISOString().split('T')[0],
          manager: '',
          skills: [],
          certifications: [],
          experience_years: 0,
          company_name: companyName.trim(),
          office_location: '',
          team: department.trim(),
          role: signupType === 'new_company' ? 'admin' : userRole,
          access_level: signupType === 'new_company' ? 'admin' : 'employee',
          status: 'active',
          company_id: 1,
          preferences: {
            notifications: {
              email: true,
              push: true,
              sms: false,
              marketing: false,
              security_alerts: true,
              job_updates: true,
              team_updates: true
            },
            privacy: {
              profile_visibility: 'team',
              contact_visibility: 'managers',
              activity_visibility: 'private',
              search_visibility: true,
              data_sharing: false
            },
            security: {
              two_factor_enabled: false,
              biometric_enabled: false,
              session_timeout: 30,
              login_alerts: true,
              device_tracking: true
            }
          }
        };

        // Prepare subscription data if new company
        let subscriptionData = null;
        if (signupType === 'new_company' && selectedPlan) {
          const selectedPlanData = subscriptionPlans.find(p => p.id === selectedPlan);
          subscriptionData = {
            plan_id: selectedPlan,
            plan_name: selectedPlanData.name,
            price: selectedPlanData.price,
            users: selectedPlanData.users,
            features: selectedPlanData.features,
            limits: {
              jobs: 'unlimited',
              applicants: 'unlimited',
              interviews: 'unlimited',
              storage: selectedPlanData.users === 'Unlimited' ? 'unlimited' : '100GB',
              support: selectedPlan === 'multi_enterprise' ? 'premium_24_7' : 'priority'
            }
          };
        }

        let savedUser = null;
        let savedCompany = null;

        // Save to database if online and have token
        if (token && isOnline && apiAvailable) {
          savedUser = await saveUserToDatabase(userData, token);
          savedCompany = await saveCompanyToDatabase(companyData, token);
          
          if (subscriptionData) {
            await saveSubscriptionToDatabase(subscriptionData, savedCompany?.id || companyId, token);
          }
          
          if (teamMembers.length > 0) {
            await saveTeamMembersToDatabase(teamMembers, savedCompany?.id || companyId, token);
          }
        }

        // Save to context (local storage)
        await saveUser(savedUser || userData);
        await saveCompanyProfile(savedCompany || companyData);
        
        if (subscriptionData) {
          await saveSubscription(subscriptionData);
        }

        for (const member of teamMembers) {
          await contextAddTeamMember({
            name: member.email.split('@')[0],
            email: member.email,
            position: member.role,
            department: member.department,
            role: member.role,
            status: 'invited',
            invitedBy: cleanUserEmail,
            invitedAt: new Date().toISOString()
          });
        }

        // Add welcome notification
        await addNotification({
          title: 'Welcome to JobEng!',
          message: `Welcome ${fullName}! Your ${signupType === 'new_company' ? 'company' : 'account'} has been set up successfully.`,
          type: 'welcome',
          priority: 'high'
        });

        // Store auth data
        if (token) {
          await storage.setItem('auth_token', token);
        }
        
        const finalUserId = savedUser?.id || authUserId || Date.now();
        const finalCompanyId = savedCompany?.id || companyId;
        
        await storage.setItem('user_id', String(finalUserId));
        await storage.setItem('company_id', String(finalCompanyId));
        await storage.setObject('user_data', savedUser || userData);

        // Store signup data
        await storage.setObject('multi_user_business_signup', {
          companyName: companyName.trim(),
          companyEmail: cleanCompanyEmail,
          industry: industry.trim(),
          companySize: companySize.trim(),
          teamSize: companySize.trim(),
          adminEmail: cleanUserEmail,
          companyCode: newCompanyCode,
          teamMembers,
          selectedPlan,
          fullName: fullName.trim(),
          jobTitle: jobTitle.trim(),
          department: department.trim(),
          userRole: signupType === 'new_company' ? 'admin' : userRole,
          createdAt: new Date().toISOString()
        });

        // Trigger sync if online
        if (isOnline && apiAvailable) {
          await syncAllData();
        }

        const successMessage = signupType === 'new_company'
          ? `Your multi-user business account has been created successfully.${
              teamMembers.length > 0 ? `\n\nInvitations have been sent to ${teamMembers.length} team member(s).` : ''
            }\n\nCompany Code: ${newCompanyCode}`
          : `You have successfully joined ${companyName}.${
              teamMembers.length > 0 ? `\n\nInvitations have been sent to ${teamMembers.length} team member(s).` : ''
            }`;

        Alert.alert(
          'Success!',
          successMessage,
          [
            {
              text: 'Continue to Company Profile',
              onPress: () => navigation.navigate('CompanyProfileScreen', {
                companyProfile: savedCompany || companyData
              })
            }
          ]
        );

      } else {
        Alert.alert('Registration Failed', registerResult.error || 'Unable to create account');
      }

    } catch (error) {
      console.error('Signup error:', error);
      
      let errorMessage = 'Failed to create account. ';
      if (error.message.includes('already exists')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
        Alert.alert(
          'Account Exists',
          errorMessage,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Sign In', onPress: () => navigation.navigate('BusinessLogin') }
          ]
        );
        return;
      } else {
        errorMessage += error.message;
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <View style={styles.stepIndicator}>
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((stepNumber) => (
        <View key={stepNumber} style={styles.stepIndicatorContainer}>
          <View style={[styles.stepCircle, step >= stepNumber && styles.stepCircleActive]}>
            <Text style={[styles.stepNumber, step >= stepNumber && styles.stepNumberActive]}>
              {stepNumber}
            </Text>
          </View>
          {stepNumber < totalSteps && (
            <View style={[styles.stepLine, step > stepNumber && styles.stepLineActive]} />
          )}
        </View>
      ))}
    </View>
  );

  const renderStep1 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Company Information</Text>
      <Text style={styles.stepSubtitle}>
        {signupType === 'new_company'
          ? 'Set up your new company account'
          : 'Join an existing company'
        }
      </Text>

      <View style={styles.signupTypeContainer}>
        <TouchableOpacity
          style={[styles.signupTypeButton, signupType === 'new_company' && styles.signupTypeButtonActive]}
          onPress={() => {
            setSignupType('new_company');
            setStep(1);
          }}
        >
          <MaterialCommunityIcons
            name="office-building-plus"
            size={24}
            color={signupType === 'new_company' ? '#0e4e96' : '#64748B'}
          />
          <Text style={[styles.signupTypeText, signupType === 'new_company' && styles.signupTypeTextActive]}>
            Create New Company
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.signupTypeButton, signupType === 'join_company' && styles.signupTypeButtonActive]}
          onPress={() => {
            setSignupType('join_company');
            setStep(1);
          }}
        >
          <MaterialCommunityIcons
            name="account-group-outline"
            size={24}
            color={signupType === 'join_company' ? '#0e4e96' : '#64748B'}
          />
          <Text style={[styles.signupTypeText, signupType === 'join_company' && styles.signupTypeTextActive]}>
            Join Existing Company
          </Text>
        </TouchableOpacity>
      </View>

      {signupType === 'new_company' ? (
        <>
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Company Name *</Text>
            <View style={[
              styles.inputWrapper,
              isFocused.companyName && styles.inputWrapperFocused,
              formErrors.companyName && styles.inputWrapperError,
            ]}>
              <MaterialCommunityIcons name="office-building" size={20} color={
                formErrors.companyName ? '#F44336' : isFocused.companyName ? '#0e4e96' : '#64748B'
              } />
              <TextInput
                style={styles.input}
                placeholder="Enter company name"
                value={companyName}
                onChangeText={(val) => handleInputChange('companyName', val)}
                onFocus={() => handleFocus('companyName')}
                onBlur={() => handleBlur('companyName')}
                placeholderTextColor="#94A3B8"
              />
            </View>
            {formErrors.companyName && <Text style={styles.errorText}>{formErrors.companyName}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Company Email *</Text>
            <View style={[
              styles.inputWrapper,
              isFocused.companyEmail && styles.inputWrapperFocused,
              formErrors.companyEmail && styles.inputWrapperError,
            ]}>
              <MaterialCommunityIcons name="email" size={20} color={
                formErrors.companyEmail ? '#F44336' : isFocused.companyEmail ? '#0e4e96' : '#64748B'
              } />
              <TextInput
                style={styles.input}
                placeholder="Enter company email"
                value={companyEmail}
                onChangeText={(val) => handleInputChange('companyEmail', val)}
                keyboardType="email-address"
                autoCapitalize="none"
                onFocus={() => handleFocus('companyEmail')}
                onBlur={() => handleBlur('companyEmail')}
                placeholderTextColor="#94A3B8"
              />
            </View>
            {formErrors.companyEmail && <Text style={styles.errorText}>{formErrors.companyEmail}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Industry *</Text>
            <View style={[
              styles.inputWrapper,
              isFocused.industry && styles.inputWrapperFocused,
              formErrors.industry && styles.inputWrapperError,
            ]}>
              <MaterialCommunityIcons name="domain" size={20} color={
                formErrors.industry ? '#F44336' : isFocused.industry ? '#0e4e96' : '#64748B'
              } />
              <TouchableOpacity
                style={styles.dropdownButton}
                onPress={() => {
                  Alert.alert(
                    'Select Industry',
                    '',
                    industries.map(ind => ({
                      text: ind,
                      onPress: () => handleInputChange('industry', ind)
                    })),
                    { cancelable: true }
                  );
                }}
              >
                <Text style={[styles.dropdownText, !industry && styles.placeholderText]}>
                  {industry || 'Select Industry'}
                </Text>
                <Ionicons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>
            {formErrors.industry && <Text style={styles.errorText}>{formErrors.industry}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Company Size *</Text>
            <View style={[
              styles.inputWrapper,
              isFocused.companySize && styles.inputWrapperFocused,
              formErrors.companySize && styles.inputWrapperError,
            ]}>
              <MaterialCommunityIcons name="account-group" size={20} color={
                formErrors.companySize ? '#F44336' : isFocused.companySize ? '#0e4e96' : '#64748B'
              } />
              <TouchableOpacity
                style={styles.dropdownButton}
                onPress={() => {
                  Alert.alert(
                    'Select Company Size',
                    '',
                    companySizes.map(size => ({
                      text: size,
                      onPress: () => handleInputChange('companySize', size)
                    })),
                    { cancelable: true }
                  );
                }}
              >
                <Text style={[styles.dropdownText, !companySize && styles.placeholderText]}>
                  {companySize || 'Select Company Size'}
                </Text>
                <Ionicons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>
            {formErrors.companySize && <Text style={styles.errorText}>{formErrors.companySize}</Text>}
          </View>
        </>
      ) : (
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Company Code</Text>
          <View style={[
            styles.inputWrapper,
            isFocused.companyCode && styles.inputWrapperFocused,
          ]}>
            <MaterialCommunityIcons name="key" size={20} color={isFocused.companyCode ? '#0e4e96' : '#64748B'} />
            <TextInput
              style={styles.input}
              placeholder="Enter 8-character company code"
              value={companyCode}
              onChangeText={(val) => handleInputChange('companyCode', val.toUpperCase())}
              autoCapitalize="characters"
              maxLength={8}
              onFocus={() => handleFocus('companyCode')}
              onBlur={() => handleBlur('companyCode')}
              placeholderTextColor="#94A3B8"
            />
            {isValidatingCode && <ActivityIndicator size="small" color="#0e4e96" />}
          </View>
          <Text style={styles.helperText}>
            Enter the 8-character code provided by your company administrator
          </Text>
        </View>
      )}
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>User Information</Text>
      <Text style={styles.stepSubtitle}>Set up your personal account details</Text>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Full Name *</Text>
        <View style={[
          styles.inputWrapper,
          isFocused.fullName && styles.inputWrapperFocused,
          formErrors.fullName && styles.inputWrapperError,
        ]}>
          <MaterialCommunityIcons name="account" size={20} color={
            formErrors.fullName ? '#F44336' : isFocused.fullName ? '#0e4e96' : '#64748B'
          } />
          <TextInput
            style={styles.input}
            placeholder="Enter your full name"
            value={fullName}
            onChangeText={(val) => handleInputChange('fullName', val)}
            onFocus={() => handleFocus('fullName')}
            onBlur={() => handleBlur('fullName')}
            placeholderTextColor="#94A3B8"
          />
        </View>
        {formErrors.fullName && <Text style={styles.errorText}>{formErrors.fullName}</Text>}
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Email Address *</Text>
        <View style={[
          styles.inputWrapper,
          isFocused.userEmail && styles.inputWrapperFocused,
          formErrors.userEmail && styles.inputWrapperError,
        ]}>
          <MaterialCommunityIcons name="email" size={20} color={
            formErrors.userEmail ? '#F44336' : isFocused.userEmail ? '#0e4e96' : '#64748B'
          } />
          <TextInput
            style={styles.input}
            placeholder="Enter your email"
            value={userEmail}
            onChangeText={(val) => handleInputChange('userEmail', val)}
            keyboardType="email-address"
            autoCapitalize="none"
            onFocus={() => handleFocus('userEmail')}
            onBlur={() => handleBlur('userEmail')}
            placeholderTextColor="#94A3B8"
          />
        </View>
        {formErrors.userEmail && <Text style={styles.errorText}>{formErrors.userEmail}</Text>}
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Job Title *</Text>
        <View style={[
          styles.inputWrapper,
          isFocused.jobTitle && styles.inputWrapperFocused,
          formErrors.jobTitle && styles.inputWrapperError,
        ]}>
          <MaterialCommunityIcons name="briefcase" size={20} color={
            formErrors.jobTitle ? '#F44336' : isFocused.jobTitle ? '#0e4e96' : '#64748B'
          } />
          <TextInput
            style={styles.input}
            placeholder="Enter your job title"
            value={jobTitle}
            onChangeText={(val) => handleInputChange('jobTitle', val)}
            onFocus={() => handleFocus('jobTitle')}
            onBlur={() => handleBlur('jobTitle')}
            placeholderTextColor="#94A3B8"
          />
        </View>
        {formErrors.jobTitle && <Text style={styles.errorText}>{formErrors.jobTitle}</Text>}
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Department *</Text>
        <View style={[
          styles.inputWrapper,
          isFocused.department && styles.inputWrapperFocused,
          formErrors.department && styles.inputWrapperError,
        ]}>
          <MaterialCommunityIcons name="domain" size={20} color={
            formErrors.department ? '#F44336' : isFocused.department ? '#0e4e96' : '#64748B'
          } />
          <TextInput
            style={styles.input}
            placeholder="Enter your department"
            value={department}
            onChangeText={(val) => handleInputChange('department', val)}
            onFocus={() => handleFocus('department')}
            onBlur={() => handleBlur('department')}
            placeholderTextColor="#94A3B8"
          />
        </View>
        {formErrors.department && <Text style={styles.errorText}>{formErrors.department}</Text>}
      </View>

      {signupType === 'join_company' && (
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Your Role</Text>
          <View style={styles.inputWrapper}>
            <MaterialCommunityIcons name="account-cog" size={20} color="#64748B" />
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => {
                Alert.alert(
                  'Select Role',
                  '',
                  userRoles.filter(r => r.id !== 'admin').map(role => ({
                    text: role.title,
                    onPress: () => setUserRole(role.id)
                  })),
                  { cancelable: true }
                );
              }}
            >
              <Text style={[styles.dropdownText, styles.placeholderText]}>
                {userRoles.find(r => r.id === userRole)?.title || 'Select Role'}
              </Text>
              <Ionicons name="chevron-down" size={20} color="#64748B" />
            </TouchableOpacity>
          </View>
        </View>
      )}

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Password *</Text>
        <View style={[
          styles.inputWrapper,
          isFocused.password && styles.inputWrapperFocused,
          formErrors.password && styles.inputWrapperError,
        ]}>
          <MaterialCommunityIcons name="lock" size={20} color={
            formErrors.password ? '#F44336' : isFocused.password ? '#0e4e96' : '#64748B'
          } />
          <TextInput
            style={styles.input}
            placeholder="Enter password (min 6 characters)"
            value={password}
            onChangeText={(val) => handleInputChange('password', val)}
            secureTextEntry={!showPassword}
            onFocus={() => handleFocus('password')}
            onBlur={() => handleBlur('password')}
            placeholderTextColor="#94A3B8"
          />
          <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
            <Ionicons name={showPassword ? "eye-off" : "eye"} size={20} color="#64748B" />
          </TouchableOpacity>
        </View>
        {formErrors.password && <Text style={styles.errorText}>{formErrors.password}</Text>}
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Confirm Password *</Text>
        <View style={[
          styles.inputWrapper,
          isFocused.confirmPassword && styles.inputWrapperFocused,
          (formErrors.confirmPassword || (confirmPassword && password !== confirmPassword)) && styles.inputWrapperError,
        ]}>
          <MaterialCommunityIcons name="lock-check" size={20} color={
            formErrors.confirmPassword ? '#F44336' : isFocused.confirmPassword ? '#0e4e96' : '#64748B'
          } />
          <TextInput
            style={styles.input}
            placeholder="Confirm your password"
            value={confirmPassword}
            onChangeText={(val) => handleInputChange('confirmPassword', val)}
            secureTextEntry={!showConfirmPassword}
            onFocus={() => handleFocus('confirmPassword')}
            onBlur={() => handleBlur('confirmPassword')}
            placeholderTextColor="#94A3B8"
          />
          <TouchableOpacity onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
            <Ionicons name={showConfirmPassword ? "eye-off" : "eye"} size={20} color="#64748B" />
          </TouchableOpacity>
        </View>
        {formErrors.confirmPassword && <Text style={styles.errorText}>{formErrors.confirmPassword}</Text>}
        {confirmPassword && password !== confirmPassword && (
          <Text style={styles.errorText}>Passwords do not match</Text>
        )}
      </View>

      <View style={styles.passwordRequirements}>
        <Text style={styles.passwordRequirementsTitle}>Password must contain:</Text>
        <View style={styles.passwordRequirement}>
          <Ionicons name={password.length >= 6 ? "checkmark-circle" : "ellipse-outline"} size={16} color={password.length >= 6 ? "#10B981" : "#64748B"} />
          <Text style={styles.passwordRequirementText}>At least 6 characters</Text>
        </View>
        <View style={styles.passwordRequirement}>
          <Ionicons name={/[A-Z]/.test(password) ? "checkmark-circle" : "ellipse-outline"} size={16} color={/[A-Z]/.test(password) ? "#10B981" : "#64748B"} />
          <Text style={styles.passwordRequirementText}>At least one uppercase letter</Text>
        </View>
        <View style={styles.passwordRequirement}>
          <Ionicons name={/[a-z]/.test(password) ? "checkmark-circle" : "ellipse-outline"} size={16} color={/[a-z]/.test(password) ? "#10B981" : "#64748B"} />
          <Text style={styles.passwordRequirementText}>At least one lowercase letter</Text>
        </View>
        <View style={styles.passwordRequirement}>
          <Ionicons name={/[0-9]/.test(password) ? "checkmark-circle" : "ellipse-outline"} size={16} color={/[0-9]/.test(password) ? "#10B981" : "#64748B"} />
          <Text style={styles.passwordRequirementText}>At least one number</Text>
        </View>
      </View>
    </View>
  );

  const renderStep3 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Team Setup</Text>
      <Text style={styles.stepSubtitle}>Invite team members to join your company (optional)</Text>

      <View style={styles.teamMemberForm}>
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Team Member Email</Text>
          <View style={[
            styles.inputWrapper,
            isFocused.newMemberEmail && styles.inputWrapperFocused,
          ]}>
            <MaterialCommunityIcons name="email-plus" size={20} color={isFocused.newMemberEmail ? '#0e4e96' : '#64748B'} />
            <TextInput
              style={styles.input}
              placeholder="Enter email"
              value={newMemberEmail}
              onChangeText={(val) => handleInputChange('newMemberEmail', val)}
              keyboardType="email-address"
              autoCapitalize="none"
              onFocus={() => handleFocus('newMemberEmail')}
              onBlur={() => handleBlur('newMemberEmail')}
              placeholderTextColor="#94A3B8"
            />
          </View>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Department (optional)</Text>
          <View style={[
            styles.inputWrapper,
            isFocused.newMemberDepartment && styles.inputWrapperFocused,
          ]}>
            <MaterialCommunityIcons name="domain" size={20} color={isFocused.newMemberDepartment ? '#0e4e96' : '#64748B'} />
            <TextInput
              style={styles.input}
              placeholder="Enter department"
              value={newMemberDepartment}
              onChangeText={(val) => handleInputChange('newMemberDepartment', val)}
              onFocus={() => handleFocus('newMemberDepartment')}
              onBlur={() => handleBlur('newMemberDepartment')}
              placeholderTextColor="#94A3B8"
            />
          </View>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Role</Text>
          <View style={styles.inputWrapper}>
            <MaterialCommunityIcons name="account-cog" size={20} color="#64748B" />
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => {
                Alert.alert(
                  'Select Role',
                  '',
                  userRoles.filter(r => r.id !== 'admin').map(role => ({
                    text: role.title,
                    onPress: () => setNewMemberRole(role.id)
                  })),
                  { cancelable: true }
                );
              }}
            >
              <Text style={[styles.dropdownText, styles.placeholderText]}>
                {userRoles.find(r => r.id === newMemberRole)?.title || 'Select Role'}
              </Text>
              <Ionicons name="chevron-down" size={20} color="#64748B" />
            </TouchableOpacity>
          </View>
        </View>

        <TouchableOpacity style={styles.addMemberButton} onPress={addTeamMemberToState}>
          <MaterialCommunityIcons name="account-plus" size={20} color="#FFFFFF" />
          <Text style={styles.addMemberButtonText}>Add Team Member</Text>
        </TouchableOpacity>
      </View>

      {teamMembers.length > 0 && (
        <View style={styles.teamMembersList}>
          <Text style={styles.teamMembersTitle}>Team Members ({teamMembers.length})</Text>
          {teamMembers.map((member) => (
            <View key={member.id} style={styles.teamMemberItem}>
              <View style={styles.teamMemberInfo}>
                <Text style={styles.teamMemberEmail}>{member.email}</Text>
                <Text style={styles.teamMemberRole}>
                  {userRoles.find(r => r.id === member.role)?.title} • {member.department}
                </Text>
              </View>
              <TouchableOpacity onPress={() => removeTeamMemberFromState(member.id)} style={styles.removeMemberButton}>
                <MaterialCommunityIcons name="close" size={20} color="#F44336" />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const renderStep4 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Choose Your Plan</Text>
      <Text style={styles.stepSubtitle}>Select the subscription plan that best fits your team</Text>

      <ScrollView showsVerticalScrollIndicator={false}>
        {subscriptionPlans.map((plan) => (
          <TouchableOpacity
            key={plan.id}
            style={[
              styles.planCard,
              selectedPlan === plan.id && styles.planCardSelected,
              plan.popular && styles.planCardPopular
            ]}
            onPress={() => setSelectedPlan(plan.id)}
          >
            {plan.popular && (
              <View style={styles.popularBadge}>
                <Text style={styles.popularBadgeText}>MOST POPULAR</Text>
              </View>
            )}
            <View style={styles.planHeader}>
              <Text style={styles.planName}>{plan.name}</Text>
              <View style={styles.planPrice}>
                <Text style={styles.planPriceAmount}>£{plan.price}</Text>
                <Text style={styles.planPricePeriod}>/month</Text>
              </View>
            </View>
            <Text style={styles.planUsers}>
              <Text>{plan.users === 'Unlimited' ? 'Unlimited users' : `Up to ${plan.users} users`}</Text>
            </Text>
            <View style={styles.planFeatures}>
              {plan.features.map((feature, index) => (
                <View key={index} style={styles.planFeature}>
                  <MaterialCommunityIcons name="check" size={16} color="#4CAF50" />
                  <Text style={styles.planFeatureText}>{feature}</Text>
                </View>
              ))}
            </View>
            {selectedPlan === plan.id && (
              <View style={styles.selectedIndicator}>
                <MaterialCommunityIcons name="check-circle" size={24} color="#0e4e96" />
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.container}>
            {/* Header */}
            <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.headerGradient}>
              <View style={styles.headerContainer}>
                <Image
                  source={require('../../../../assets/jobeng_full_logo.png')}
                  style={styles.logo}
                  resizeMode="contain"
                />
                <Text style={styles.subtitle}>UK Engineering Excellence</Text>
                <Text style={styles.headerDescription}>Multi-User Business Setup</Text>

                <View style={styles.connectionStatusContainer}>
                  <Ionicons
                    name={serverStatus === 'connected' ? "cloud-done" : serverStatus === 'error' ? "cloud-offline" : "cloud-outline"}
                    size={16}
                    color={serverStatus === 'connected' ? "#4CAF50" : serverStatus === 'error' ? "#F44336" : "#FFC107"}
                  />
                  <Text style={styles.connectionStatusText}>
                    <Text>{serverStatus === 'connected' ? 'Connected' : serverStatus === 'error' ? 'Connection Error' : 'Checking...'}</Text>
                  </Text>
                </View>

                <TouchableOpacity style={styles.testConnectionButton} onPress={testServerConnection}>
                  <Text style={styles.testConnectionText}>Test Server Connection</Text>
                </TouchableOpacity>

                {renderStepIndicator()}
              </View>
            </LinearGradient>

            {/* Form */}
            <View style={styles.formContainer}>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </View>

            {/* Navigation */}
            <View style={styles.navigationButtons}>
              {step > 1 && (
                <TouchableOpacity
                  style={[styles.navButton, styles.previousButton]}
                  onPress={handlePrevious}
                  disabled={isLoading || isValidatingCode}
                >
                  <Text style={styles.previousButtonText}>Previous</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[
                  styles.navButton,
                  styles.nextButton,
                  step === 1 && styles.nextButtonFull,
                  (isLoading || isValidatingCode) && styles.nextButtonDisabled,
                ]}
                onPress={handleNext}
                disabled={isLoading || isValidatingCode}
              >
                {isLoading || isValidatingCode ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.nextButtonText}>
                      <Text>{step === totalSteps ? 'Create Account' : 'Next'}</Text>
                    </Text>
                    <Ionicons
                      name={step === totalSteps ? "checkmark" : "arrow-forward"}
                      size={20}
                      color="#FFFFFF"
                    />
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  headerGradient: {
    paddingTop: 20,
    paddingBottom: 30,
    paddingHorizontal: 24,
  },
  headerContainer: {
    alignItems: 'center',
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 12,
    tintColor: '#fff',
  },
  subtitle: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '500',
    marginBottom: 4,
  },
  headerDescription: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center',
    marginBottom: 10,
  },
  connectionStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
  },
  connectionStatusText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginLeft: 4,
  },
  testConnectionButton: {
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 16,
  },
  testConnectionText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 15,
  },
  stepIndicatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCircleActive: {
    backgroundColor: '#FFFFFF',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  stepNumberActive: {
    color: '#0e4e96',
  },
  stepLine: {
    width: 30,
    height: 2,
    backgroundColor: 'rgba(255,255,255,0.3)',
    marginHorizontal: 6,
  },
  stepLineActive: {
    backgroundColor: '#FFFFFF',
  },
  formContainer: {
    backgroundColor: '#F8FAFC',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    marginTop: -20,
    padding: 24,
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  stepContainer: {
    marginBottom: 20,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  stepSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 20,
  },
  signupTypeContainer: {
    flexDirection: 'row',
    marginBottom: 24,
    backgroundColor: '#E2E8F0',
    borderRadius: 12,
    padding: 4,
  },
  signupTypeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  signupTypeButtonActive: {
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  signupTypeText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 6,
  },
  signupTypeTextActive: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 6,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  inputWrapperFocused: {
    borderColor: '#0e4e96',
    backgroundColor: '#FFFFFF',
  },
  inputWrapperError: {
    borderColor: '#F44336',
    backgroundColor: '#FFEBEE',
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: '#1E293B',
    marginLeft: 8,
    padding: 0,
  },
  dropdownButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: 8,
  },
  dropdownText: {
    fontSize: 14,
    color: '#1E293B',
  },
  placeholderText: {
    color: '#94A3B8',
  },
  errorText: {
    fontSize: 12,
    color: '#F44336',
    marginTop: 4,
    marginLeft: 4,
  },
  helperText: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
    marginLeft: 4,
  },
  passwordRequirements: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  passwordRequirementsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  passwordRequirement: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  passwordRequirementText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 8,
  },
  teamMemberForm: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  addMemberButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  addMemberButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  teamMembersList: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  teamMembersTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 12,
  },
  teamMemberItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  teamMemberInfo: {
    flex: 1,
  },
  teamMemberEmail: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 2,
  },
  teamMemberRole: {
    fontSize: 12,
    color: '#64748B',
  },
  removeMemberButton: {
    padding: 4,
  },
  planCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    position: 'relative',
  },
  planCardSelected: {
    borderColor: '#0e4e96',
  },
  planCardPopular: {
    borderColor: '#10B981',
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    left: 20,
    backgroundColor: '#10B981',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  popularBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
  planHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  planName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
  },
  planPrice: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  planPriceAmount: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0e4e96',
  },
  planPricePeriod: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 2,
  },
  planUsers: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 12,
  },
  planFeatures: {
    marginBottom: 8,
  },
  planFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  planFeatureText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 8,
    flex: 1,
  },
  selectedIndicator: {
    position: 'absolute',
    top: 12,
    right: 12,
  },
  navigationButtons: {
    flexDirection: 'row',
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 8,
    backgroundColor: '#F8FAFC',
  },
  navButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  previousButton: {
    backgroundColor: '#F3F4F6',
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  previousButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  nextButton: {
    backgroundColor: '#0e4e96',
  },
  nextButtonFull: {
    flex: 1,
    marginLeft: 0,
  },
  nextButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  nextButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 6,
  },
});